import React from 'react';

interface Step2B_RiskComfortProps {
  selectedRisk: 'low' | 'medium' | 'high' | null;
  onSelect: (risk: 'low' | 'medium' | 'high') => void;
}

const Step2B_RiskComfort: React.FC<Step2B_RiskComfortProps> = ({ selectedRisk, onSelect }) => {
  const getRiskDetails = (level: 'low' | 'medium' | 'high') => {
    switch (level) {
      case 'low':
        return {
          title: 'Conservative',
          subtitle: 'Focus on keeping shares',
          dte: '45-90 days',
          delta: 'Delta 0.05-0.15',
          description: 'Conservative approach with lower assignment risk'
        };
      case 'medium':
        return {
          title: 'Balanced',
          subtitle: 'Balanced income and retention',
          dte: '30-45 days',
          delta: 'Delta 0.15-0.25',
          description: 'Sweet spot for most covered call traders'
        };
      case 'high':
        return {
          title: 'Aggressive',
          subtitle: 'Maximize income potential',
          dte: '7-30 days',
          delta: 'Delta 0.25-0.40',
          description: 'Higher premiums with increased assignment risk'
        };
    }
  };

  return (
    <div className="wizard-step">
      <div className="wizard-step-content">
        <h2 className="wizard-question">
          Select Strategy Aggressiveness
        </h2>

        <div className="wizard-risk-slider-container">
          {/* Three-option pill selector styled as slider */}
          <div className="wizard-risk-options">
            <button
              className={`wizard-risk-option ${selectedRisk === 'low' ? 'selected' : ''}`}
              onClick={() => onSelect('low')}
              aria-pressed={selectedRisk === 'low'}
            >
              <div className="risk-option-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div className="risk-option-label">Conservative</div>
              <div className="risk-option-subtext">Focus on OTM strikes, higher probability of profit, lower premium</div>
            </button>

            <button
              className={`wizard-risk-option ${selectedRisk === 'medium' ? 'selected' : ''}`}
              onClick={() => onSelect('medium')}
              aria-pressed={selectedRisk === 'medium'}
            >
              <div className="risk-option-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3 8-8" />
                  <path d="M20 12v6a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h9" />
                </svg>
              </div>
              <div className="risk-option-label">Balanced</div>
              <div className="risk-option-subtext">Near-the-money strikes, balanced risk/reward</div>
            </button>

            <button
              className={`wizard-risk-option ${selectedRisk === 'high' ? 'selected' : ''}`}
              onClick={() => onSelect('high')}
              aria-pressed={selectedRisk === 'high'}
            >
              <div className="risk-option-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <div className="risk-option-label">Aggressive</div>
              <div className="risk-option-subtext">Focus on high volatility or ITM/ATM, maximum premium focus</div>
            </button>
          </div>

          {/* Risk level details */}
          {selectedRisk && (
            <div className="wizard-risk-details">
              <div className="risk-details-header">
                <h3>{getRiskDetails(selectedRisk).title}</h3>
                <p>{getRiskDetails(selectedRisk).subtitle}</p>
              </div>
              <div className="risk-details-specs">
                <div className="risk-spec">
                  <span className="spec-label">Contract Length:</span>
                  <span className="spec-value">{getRiskDetails(selectedRisk).dte}</span>
                </div>
                <div className="risk-spec">
                  <span className="spec-label">Strike Selection:</span>
                  <span className="spec-value">{getRiskDetails(selectedRisk).delta}</span>
                </div>
              </div>
              <p className="risk-details-description">
                {getRiskDetails(selectedRisk).description}
              </p>
            </div>
          )}
        </div>

        <p className="wizard-helper-text">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{display: 'inline', marginRight: '6px', verticalAlign: 'text-bottom'}}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
          This determines which options contracts we'll show you for each stock.
        </p>
      </div>
    </div>
  );
};

export default Step2B_RiskComfort;
